import React, { useState } from "react";
import axios from "axios";
import { Leaf, Phone, Mail, MapPin, Loader2, Facebook } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SITE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    }
    setLoading(false);
  };

  const links = [
    { label: "Services", href: "#services" },
    { label: "Why Us", href: "#why-us" },
    { label: "Pricing", href: "#pricing" },
    { label: "Projects", href: "#projects" },
    { label: "Get a Quote", href: "#quote" },
  ];
  void links;

  return (
    <footer data-testid="main-footer" className="bg-primary text-primary-foreground">
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-[hsl(var(--brand-green))]/20 text-[hsl(var(--brand-green))]">
                <Leaf className="w-5 h-5" />
              </span>
              <span className="font-semibold tracking-tight text-xl">{SITE.name}</span>
            </div>
            <p className="mt-4 text-primary-foreground/70 max-w-sm">
              {SITE.tagline} {SITE.subTagline} Professional junk removal & property cleanouts serving Edmonton and surrounding areas.
            </p>
            <div className="mt-6 space-y-2 text-sm text-primary-foreground/80">
              {SITE.contacts.map((c) => (
                <a key={c.tel} href={`tel:${c.tel}`} className="flex items-center gap-2 hover:text-secondary transition-colors">
                  <Phone className="w-4 h-4" /> {c.name}: {c.phone}
                </a>
              ))}
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-secondary transition-colors break-all">
                <Mail className="w-4 h-4 shrink-0" /> {SITE.email}
              </a>
              <p className="flex items-center gap-2"><Facebook className="w-4 h-4" /> {SITE.facebook}</p>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-semibold tracking-tight text-lg">Service Areas</h4>
            <ul className="mt-4 space-y-2">
              {SITE.serviceAreas.map((a) => (
                <li key={a} className="flex items-center gap-2 text-primary-foreground/70 text-sm">
                  <MapPin className="w-4 h-4 text-[hsl(var(--brand-green))]" /> {a}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-semibold tracking-tight text-lg">Stay in the loop</h4>
            <p className="mt-4 text-sm text-primary-foreground/70">Tips on decluttering & seasonal cleanup offers.</p>
            <form data-testid="newsletter-form" onSubmit={subscribe} className="mt-4 flex gap-2">
              <Input
                data-testid="newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="rounded-sm bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="rounded-sm bg-white text-primary hover:bg-secondary shrink-0"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign up"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-primary-foreground/70">
              <span className="font-medium text-primary-foreground">Hours:</span> {SITE.hours}
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-sm text-primary-foreground/60">
          <p>© {new Date().getFullYear()} {SITE.name}. All Rights Reserved.</p>
          <p>Locally owned & operated in {SITE.location}.</p>
        </div>
      </div>
    </footer>
  );
};
